import React, { useEffect, useMemo } from "react";

/**
 * ToriiTransition — transition cinématique d'entrée dans une Porte.
 * Une porte torii s'ouvre en deux, révélant le kanji de l'élément.
 * CHAQUE porte a sa propre direction artistique : couleur, environnement et motif.
 */
const ELEMENTS = {
  1: { color: "#ff4d1c", glow: "#ffb347", kind: "ember",  label: "Le Feu",       env: "radial-gradient(circle at 50% 60%, #7a1400 0%, #2a0700 55%, #0a0302 100%)" },   // 火
  2: { color: "#2fae5f", glow: "#b6f0c2", kind: "leaf",   label: "La Vie",       env: "radial-gradient(circle at 50% 65%, #0f5a2e 0%, #08331b 55%, #04150c 100%)" },   // 生
  3: { color: "#e11d2a", glow: "#ff8a8a", kind: "oni",    label: "Les Masques",  env: "radial-gradient(circle at 50% 55%, #6e0710 0%, #2a0207 55%, #100103 100%)" },   // 面 rouge + oni
  4: { color: "#2f7bff", glow: "#a9cbff", kind: "thread", label: "Le Fil",       env: "radial-gradient(circle at 50% 60%, #0a2f6e 0%, #05173a 55%, #020a1c 100%)" },   // 糸 bleu
  5: { color: "#9b5cf6", glow: "#d6c2ff", kind: "root",   label: "Les Racines",  env: "radial-gradient(circle at 50% 60%, #3d1a6e 0%, #200c3a 55%, #0e0520 100%)" },   // 根 violet
  6: { color: "#f4b400", glow: "#fff0b3", kind: "spark",  label: "Le Courage",   env: "radial-gradient(circle at 50% 60%, #7a5400 0%, #2e2000 55%, #120c00 100%)" },   // 勇 jaune
  7: { color: "#1fb0a6", glow: "#bff5ee", kind: "wave",   label: "L'Appel",      env: "radial-gradient(circle at 50% 70%, #0a5f66 0%, #043238 55%, #01161a 100%)" },   // 門/海 mer
};
const elementFor = (g) => ELEMENTS[g?.index] || ELEMENTS[1];

export default function ToriiTransition({ gate, onDone, duration = 2100 }) {
  const el = elementFor(gate);
  const kanji = gate?.kanji || "門";

  useEffect(() => {
    const t = setTimeout(() => onDone && onDone(), duration);
    return () => clearTimeout(t);
  }, [onDone, duration]);

  const particles = useMemo(() => Array.from({ length: 30 }).map((_, i) => ({
    i, x: Math.random() * 100,
    delay: (Math.random() * 0.9).toFixed(2),
    dur: (1.5 + Math.random() * 1.5).toFixed(2),
    size: (10 + Math.random() * 20).toFixed(0),
    drift: (Math.random() * 60 - 30).toFixed(0),
    rot: (Math.random() * 360).toFixed(0),
  })), []);

  return (
    <div style={{ ...s.overlay, background: el.env }} data-testid="torii-transition">
      {gate?.cover && <div style={{ ...s.env, backgroundImage: `url(${gate.cover})` }} />}
      <div style={{ ...s.tint, background: `radial-gradient(circle at 50% 45%, ${el.color}44, transparent 68%)` }} />

      {/* particules / motif propre à l'élément */}
      <div style={s.particles} aria-hidden>
        {particles.map((p) => (
          <Particle key={p.i} p={p} el={el} />
        ))}
      </div>

      <div style={{ ...s.light, background: `radial-gradient(circle, ${el.glow}, ${el.color} 45%, transparent 72%)` }} />

      {/* torii : deux battants qui s'écartent */}
      <div style={s.stage}>
        <div style={{ ...s.leaf, ...s.leafLeft }}><Torii side="left" color={el.color} /></div>
        <div style={{ ...s.leaf, ...s.leafRight }}><Torii side="right" color={el.color} /></div>
        <div style={s.center}>
          <div style={{ ...s.kanji, textShadow: `0 0 60px ${el.glow}, 0 0 120px ${el.color}` }}>{kanji}</div>
          <div style={{ ...s.label, color: el.glow }}>{gate?.title || el.label}</div>
        </div>
      </div>
    </div>
  );
}

function Particle({ p, el }) {
  const common = {
    position: "absolute", bottom: "-8%", left: `${p.x}%`,
    animation: `toriiRise ${p.dur}s ease-in ${p.delay}s forwards`,
    "--drift": `${p.drift}px`, opacity: 0,
  };
  if (el.kind === "oni") {
    return <span style={{ ...common, fontSize: `${Number(p.size) + 6}px`, filter: `drop-shadow(0 0 8px ${el.color})`, transform: `rotate(${p.rot}deg)` }}>👹</span>;
  }
  if (el.kind === "wave") {
    return <span style={{ ...common, fontSize: `${Number(p.size) + 4}px`, filter: `drop-shadow(0 0 8px ${el.glow})` }}>🌊</span>;
  }
  if (el.kind === "leaf") {
    return <span style={{ ...common, width: p.size / 2 + "px", height: p.size / 1.4 + "px", background: el.glow, borderRadius: "50% 0 50% 0", boxShadow: `0 0 10px ${el.color}`, transform: `rotate(${p.rot}deg)` }} />;
  }
  if (el.kind === "thread") {
    return <span style={{ ...common, width: "2px", height: p.size + "px", background: `linear-gradient(${el.glow}, transparent)`, boxShadow: `0 0 8px ${el.color}` }} />;
  }
  if (el.kind === "root") {
    return <span style={{ ...common, width: p.size / 2 + "px", height: p.size / 2 + "px", background: el.color, borderRadius: "40% 60% 55% 45%", boxShadow: `0 0 12px ${el.glow}` }} />;
  }
  // ember / spark : points lumineux
  return <span style={{ ...common, width: p.size / 2.5 + "px", height: p.size / 2.5 + "px", background: el.glow, borderRadius: "50%", boxShadow: `0 0 12px ${el.color}, 0 0 24px ${el.color}` }} />;
}

function Torii({ side, color }) {
  const base = "#5a0f0f";
  return (
    <div style={{ position: "relative", width: "50vw", height: "100vh" }}>
      <div style={{ position: "absolute", top: "16%", [side === "left" ? "right" : "left"]: 0, width: "78%", height: 42, background: `linear-gradient(#1a0808, ${base})`, borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,0.55)" }} />
      <div style={{ position: "absolute", top: "26%", [side === "left" ? "right" : "left"]: 0, width: "70%", height: 26, background: `linear-gradient(#240a0a, ${base})`, borderRadius: 4 }} />
      <div style={{ position: "absolute", top: "16%", bottom: 0, [side === "left" ? "right" : "left"]: "14%", width: 46, background: `linear-gradient(90deg,#3a0a0a,#8a1a1a,#3a0a0a)`, borderRadius: 6, boxShadow: `0 0 34px ${color}55` }} />
    </div>
  );
}

const s = {
  overlay: { position: "fixed", inset: 0, zIndex: 9999, overflow: "hidden", animation: "toriiFade .3s ease" },
  env: { position: "absolute", inset: 0, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(14px) brightness(0.42)", transform: "scale(1.15)", opacity: 0.55 },
  tint: { position: "absolute", inset: 0 },
  particles: { position: "absolute", inset: 0, pointerEvents: "none" },
  light: { position: "absolute", top: "50%", left: "50%", width: "60vmin", height: "60vmin", transform: "translate(-50%,-50%) scale(0.2)", opacity: 0, animation: "toriiLight 2.1s ease forwards", filter: "blur(6px)" },
  stage: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  leaf: { position: "absolute", top: 0, bottom: 0, width: "50vw", transformOrigin: "center" },
  leafLeft: { left: 0, animation: "toriiOpenLeft 2.1s cubic-bezier(.7,0,.3,1) forwards" },
  leafRight: { right: 0, animation: "toriiOpenRight 2.1s cubic-bezier(.7,0,.3,1) forwards" },
  center: { position: "relative", zIndex: 3, textAlign: "center", opacity: 0, animation: "toriiKanji 1.4s ease .55s forwards" },
  kanji: { fontSize: "26vmin", fontWeight: 800, lineHeight: 1, color: "#fff", fontFamily: "'Anton', sans-serif" },
  label: { fontSize: "clamp(18px,3vmin,30px)", fontWeight: 800, letterSpacing: 2, marginTop: 6, textTransform: "uppercase" },
};

if (typeof document !== "undefined" && !document.getElementById("torii-kf")) {
  const st = document.createElement("style");
  st.id = "torii-kf";
  st.textContent = `
  @keyframes toriiFade{from{opacity:0}to{opacity:1}}
  @keyframes toriiOpenLeft{0%{transform:translateX(0)}18%{transform:translateX(0)}100%{transform:translateX(-102%)}}
  @keyframes toriiOpenRight{0%{transform:translateX(0)}18%{transform:translateX(0)}100%{transform:translateX(102%)}}
  @keyframes toriiKanji{0%{opacity:0;transform:scale(.5)}55%{opacity:1;transform:scale(1.06)}100%{opacity:1;transform:scale(1)}}
  @keyframes toriiLight{0%{opacity:0;transform:translate(-50%,-50%) scale(.2)}40%{opacity:.9}100%{opacity:.5;transform:translate(-50%,-50%) scale(1.1)}}
  @keyframes toriiRise{0%{opacity:0;transform:translateY(0) translateX(0)}15%{opacity:1}100%{opacity:0;transform:translateY(-108vh) translateX(var(--drift))}}
  `;
  document.head.appendChild(st);
}
